import React, { useEffect, useState } from "react";
import { Row, Col, Card, Table } from "react-bootstrap";
import MetricCard from "./MetricCard";

const OrganizationMetrics = ({ period, reduxData, loading, getPeriodLabel, onTeamClick  }) => {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    if (reduxData && Object.keys(reduxData).length > 0) {
      setMetrics(reduxData);
    }
  }, [reduxData]);

  const getComparisonLabel = (period) => {
    const labels = {
      daily:     { current: "Today",         previous: "Yesterday",    title: "Yesterday vs Today" },
      weekly:    { current: "This Week",      previous: "Last Week",    title: "Last Week vs This Week" },
      monthly:   { current: "This Month",     previous: "Last Month",   title: "Last Month vs This Month" },
      quarterly: { current: "This Quarter",   previous: "Last Quarter", title: "Last Quarter vs This Quarter" },
      yearly:    { current: "This Year",      previous: "Last Year",    title: "Last Year vs This Year" },
    };
    return labels[period] || { current: "Current", previous: "Previous", title: "Previous vs Current" };
  };

  if (!metrics) return <div>Loading...</div>;

  const { current, previous, title } = getComparisonLabel(period);
  console.log("period received:", period);

  return (
    <div className="organization-metrics">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="fas fa-building me-2 icon-orange"></i>
          Organization Performance - {getPeriodLabel(period)}
        </h5>
        <div className="text-muted small">
          {metrics.totalTeams} Teams | {metrics.totalEmployees} Employees
        </div>
      </div>

      {/* Organization Summary Cards */}
      <Row className="mb-4">
        <Col md={2} sm={6} className="mb-3">
          <MetricCard title="Total Tickets" value={metrics.assignedTickets} icon="fa-ticket-alt" color="#0d6efd" />
        </Col>
        <Col md={2} sm={6} className="mb-3">
          <MetricCard title="Resolved" value={metrics.resolvedTickets} icon="fa-check-circle" color="#28a745" />
        </Col>
        <Col md={2} sm={6} className="mb-3">
          <MetricCard title="Open Tickets" value={metrics.openTickets} icon="fa-folder-open" color="#ffc107" />
        </Col>
        <Col md={2} sm={6} className="mb-3">
          <MetricCard title="Efficiency" value={`${metrics.organizationEfficiency}%`} icon="fa-chart-line" color="#6f42c1" />
        </Col>
        <Col md={2} sm={6} className="mb-3">
          <MetricCard title="Avg Response" value={`${metrics.avgFirstResponseTime}h`} icon="fa-clock" color="#17a2b8" />
        </Col>
      </Row>

      {/* Team Performance Table */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Team Performance</h6>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover className="mb-0">
                <thead>
                  <tr>
                    <th>Team</th>
                    <th className="text-center">Assigned</th>
                    <th className="text-center">Resolved</th>
                    <th className="text-center">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.teams.map((team) => {
                      const rate = team.assigned > 0
                        ? ((team.resolved / team.assigned) * 100).toFixed(1)
                        : 0;
                      return (
                        <tr
                          key={team.id}
                          onClick={() => onTeamClick(team.id)}   // 👈 add this
                          style={{ cursor: "pointer" }}
                        >
                          <td><i className="fas fa-users me-2 text-muted"></i>{team.name}</td>
                          <td className="text-center">{team.assigned}</td>
                          <td className="text-center">{team.resolved}</td>
                          <td className="text-center">
                            <span className={`badge bg-${rate >= 80 ? 'success' : rate >= 60 ? 'info' : 'warning'}`}>
                              {rate}%
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        {/* Category Breakdown */}
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">Tickets by Category</h6>
            </Card.Header>
            <Card.Body>
              <div className="category-chart">
                {metrics.categoryBreakdown.map((category, index) => {
                  const total = metrics.categoryBreakdown.reduce((sum, c) => sum + c.count, 0);
                  const percentage = total > 0 ? ((category.count / total) * 100).toFixed(1) : 0;
                  return (
                    <div key={index} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <div>
                          <span style={{ backgroundColor: category.color, display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', marginRight: '8px' }}></span>
                          <span className="ms-2">{category.category}</span>
                        </div>
                        <span className="font-weight-bold">{category.count} ({percentage}%)</span>
                      </div>
                      <div className="progress" style={{ height: "10px" }}>
                        <div className="progress-bar" style={{ width: `${percentage}%`, backgroundColor: category.color }}></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Categories & Trends */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">
                <i className="fas fa-chart-bar me-2"></i>Top 5 Ticket Categories
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="top-categories">
                {metrics.topCategories.map((category, index) => (
                  <div key={index} className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <div className="d-flex align-items-center">
                        <span className="badge bg-secondary me-2" style={{ width: '24px' }}>#{index + 1}</span>
                        <span style={{ backgroundColor: category.color, display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', marginRight: '8px' }}></span>
                        <span>{category.category}</span>
                      </div>
                      <div className="text-end">
                        <strong>{category.count}</strong>
                        <small className="text-muted ms-2">({category.percentage}%)</small>
                      </div>
                    </div>
                    <div className="progress" style={{ height: "8px" }}>
                      <div className="progress-bar" style={{ width: `${category.percentage}%`, backgroundColor: category.color }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>
              <h6 className="mb-0">
                <i className="fas fa-trending-up me-2"></i>
                {/* ✅ Uses dedicated title string: "Last Week vs This Week" etc. */}
                Category Trends ({title})
              </h6>
            </Card.Header>
            <Card.Body>
              <Table responsive striped hover className="mb-0">
                <thead>
                  <tr>
                    <th>Category</th>
                    {/* ✅ Previous period column first */}
                    <th className="text-center">{previous}</th>
                    <th className="text-center">{current}</th>
                    <th className="text-center">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.categoryBreakdown.map((category, index) => {
                    const trendValue = parseFloat(category.trend);
                    const isNeutral = trendValue === 0;
                    const isIncrease = trendValue > 0;
                    const trendColor = isNeutral
                      ? 'secondary'
                      : isIncrease
                        ? (trendValue > 15 ? 'danger' : 'warning')
                        : 'success';
                    const trendIcon = isIncrease
                      ? 'fa-arrow-up'
                      : isNeutral
                        ? 'fa-minus'
                        : 'fa-arrow-down';

                    return (
                      <tr key={index}>
                        <td>
                          <span style={{ backgroundColor: category.color, display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', marginRight: '6px' }}></span>
                          {category.category}
                        </td>
                        {/* ✅ Previous period data first */}
                        <td className="text-center">{category.previousPeriod}</td>
                        <td className="text-center"><strong>{category.currentPeriod}</strong></td>
                        <td className="text-center">
                          <span className={`badge bg-${trendColor}`}>
                            <i className={`fas ${trendIcon} me-1`}></i>
                            {Math.abs(trendValue).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Status Breakdown */}
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header><h6 className="mb-0">Tickets by Status</h6></Card.Header>
            <Card.Body>
              <Row>
                {metrics.statusBreakdown.map((status, index) => (
                  <Col md={2} sm={4} xs={6} key={index} className="mb-3">
                    <div className="status-card text-center p-3" style={{ borderLeft: `4px solid ${status.color}` }}>
                      <div className="status-count" style={{ color: status.color, fontSize: "2rem", fontWeight: "bold" }}>{status.count}</div>
                      <div className="status-label text-muted small">{status.status}</div>
                    </div>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default OrganizationMetrics;